import { Colli, ColliActionName } from '@frontend/colli/types';
import { EquipmentActionName, IEquipment } from '@frontend/equipment/types';
import { Identification } from '@frontend/identification/types';
import { Package, PackageActionName } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { MqttBroker } from '@frontend/pub-sub';
import { Transaction, TransactionActionName } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import { useEffect, useRef, useState } from 'react';

import { useAppSelector } from '../../redux/store';

/* eslint-disable no-unused-vars */
interface UseIdentificationProps {
    id: string;
    onShowWizard?: (
        wizard_id: string,
        functionality_id: string,
        data: {
            package?: Package;
            colli?: Colli;
            transaction?: Transaction;
            equipment?: IEquipment;
            functionality_id: string;
            wizard_id: string;
        }
    ) => void;
    onNoMatchingIdentificationFound?: (data?: any) => void;
    onUnlinkedIdentificationFound?: (data?: any) => void;
    onPackageIdentified?: (pkg: Package, ...args: any[]) => void;
    onColliIdentified?: (colli: Colli, ...args: any[]) => void;
    onTransactionIdentified?: (transaction: Transaction, ...args: any[]) => void;
    onUserIdentified?: (user: IUser, ...args: any[]) => void;
    onUserCourierIdentified?: (user: IUser, trackingNumber: string | undefined, ...args: any[]) => void;
    onBadgeIdentified?: (badge: Identification, ...args: any[]) => void;
    onEquipmentIdentified?: (equipment: IEquipment, ...args: any[]) => void;
    onProductIdentified?: (product: IProduct, ...args: any[]) => void;

    onItsmeMessage?: (message: string, data: any) => void;
}
interface UseIdentificationReturn {
    package?: Package;
    resetPackage?: () => void;
    colli?: Colli;
    resetColli?: () => void;
    transaction?: Transaction;
    resetTransaction?: () => void;
    equipment?: IEquipment;
    resetEquipment?: () => void;
}
/* eslint-enable no-unused-vars */

export const useIdentification = (props: UseIdentificationProps): UseIdentificationReturn => {
    const navigationState = useAppSelector((store) => store.navigation);
    const IotId = navigationState.iot?.id;

    const onShowWizard = (data: any) => {
        if (!data?.wizard_id) {
            console.warn('Received show wizard message without wizard id', data);
            return;
        }
        if (!data?.functionality_id) {
            console.warn('Received show wizard message without functionality id', data);
            return;
        }
        if (props.onShowWizard) props.onShowWizard(data.wizard_id, data.functionality_id, data);
    };

    const onNoMatchingIdentificationFound = (data: any) => {
        if (props.onNoMatchingIdentificationFound) props.onNoMatchingIdentificationFound(data);
    };

    const onUnlinkedIdentificationFound = (data: any) => {
        if (props.onUnlinkedIdentificationFound) props.onUnlinkedIdentificationFound(data);
    };

    const [pkg, setPackage] = useState<Package | undefined>(undefined);
    const onPackageIdentified = (data: any) => {
        if (!data.package) {
            console.warn('Received package identification without package data', data);
            return;
        }
        setPackage(data.package);
        if (props.onPackageIdentified) props.onPackageIdentified(data.package, data);
    };

    const [colli, setColli] = useState<Colli | undefined>(undefined);
    const onColliIdentified = (data: any) => {
        if (!data.colli) {
            console.warn('Received colli identification without colli data', data);
            return;
        }
        setColli(data.colli);
        if (props.onColliIdentified) props.onColliIdentified(data.colli, data);
    };

    const [transaction, setTransaction] = useState<Transaction | undefined>(undefined);
    const onTransactionIdentified = (data: any) => {
        if (!data.transaction) {
            console.warn('Received transaction identification without transaction data', data);
            return;
        }
        setTransaction(data.transaction);
        if (props.onTransactionIdentified) props.onTransactionIdentified(data.transaction, data);
    };

    const onUserIdentified = (data: any) => {
        if (!data.user) {
            console.warn('Received user identification without user data', data);
            return;
        }
        if (props.onUserIdentified) props.onUserIdentified(data.user, data);
    };

    const onUserCourierIdentified = (data: any) => {
        if (!data.user) {
            console.warn('Received user courier identification without user data', data);
            return;
        }
        const trackingNumber = typeof data?.trigger?.data?.tracking_number === 'string' ? data.trigger.data.tracking_number : undefined;
        if (props.onUserCourierIdentified) props.onUserCourierIdentified(data.user, trackingNumber, data);
    };

    const onBadgeIdentified = (data: any) => {
        if (!data.data) {
            console.warn('Received badge identification without badge data', data);
            return;
        }
        if (props.onBadgeIdentified) props.onBadgeIdentified(data.data, data);
    };

    const [equipment, setEquipment] = useState<IEquipment | undefined>(undefined);
    const onEquipmentIdentified = (data: any) => {
        if (!data.equipment) {
            console.warn('Received equipment identification without equipment data', data);
            return;
        }
        setEquipment(data.equipment);
        if (props.onEquipmentIdentified) props.onEquipmentIdentified(data.equipment, data);
    };

    const onProductIdentified = (data: any) => {
        if (!data.product) {
            console.warn('Received product identification without product data', data);
            return;
        }
        if (props.onProductIdentified) props.onProductIdentified(data.product, data);
    };

    const callback = (message: string, data: any) => {
        if (message === PackageActionName.SHOW_WIZARD) onShowWizard(data);
        if (
            message === 'no_matching_identification_found' ||
            message === 'iot_no_matching_identification_found' ||
            (message === 'inform' && data?.data?.trigger === 'iot_no_matching_identification_found')
        )
            onNoMatchingIdentificationFound(data);
        if (message === 'unlinked_identification_found') onUnlinkedIdentificationFound(data);
        if (message === PackageActionName.PACKAGE_INFORM_IOT_IDENTIFIED) onPackageIdentified(data);
        if (message === ColliActionName.COLLI_INFORM_IOT_IDENTIFIED) onColliIdentified(data);
        if (message === TransactionActionName.TRANSACTION_INFORM_IOT_IDENTIFIED) onTransactionIdentified(data);
        if (message === 'user_inform_iot_identified') onUserIdentified(data);
        if (message === 'user_courier_inform_iot_identified') onUserCourierIdentified(data);
        if (message === 'iot_badge_identified' || (message === 'inform' && data?.data?.trigger === 'iot_badge_identified')) onBadgeIdentified(data);
        if (message === EquipmentActionName.EQUIPMENT_INFORM_IOT_IDENTIFIED) onEquipmentIdentified(data);
        if (message === 'iot_product_identified') onProductIdentified(data);

        if (message.startsWith('itsme_')) props.onItsmeMessage?.(message, data);
    };

    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const stableCallback = (message: string, data: any) => callbackRef.current(message, data);

        if (IotId) {
            MqttBroker.getInstance().subscribe(props.id + '_USE_IDENTIFICATION', 'iots/' + IotId + '/iot', stableCallback);
        }

        return () => {
            if (IotId) {
                MqttBroker.getInstance().unSubscribe(props.id + '_USE_IDENTIFICATION', 'iots/' + IotId + '/iot');
            }
        };
    }, [props.id, IotId]);

    return {
        package: pkg,
        resetPackage: () => setPackage(undefined),
        colli,
        resetColli: () => setColli(undefined),
        transaction,
        resetTransaction: () => setTransaction(undefined),
        equipment,
        resetEquipment: () => setEquipment(undefined)
    };
};
